import { useEffect, useState, useMemo, PropsWithChildren } from 'react';
import cn from 'classnames';
import throttle from 'lodash/throttle';
import { useRouter } from 'next/router';
import { LanguageSwitcher, useTranslation } from '@framework/i18n';
import { ILanguageOption } from '@framework/i18n/LanguageSwitcher';
import IconLock from '@dealroadshow/uikit-icon/IconLock';
import IconUserLock from '@dealroadshow/uikit-icon/IconUserLock';
import CookiesBanner from '@framework/cookie-consent/CookiesBanner';
import useLoginUrlWithConsent from '@framework/cookie-consent/useLoginUrlWithConsent';
import Header from '@framework/ui/Pages/Landing/Legacy/Sections/Header';
import Footer from '../Footer';
import { useCustomizationContext } from '../CustomizationContext';

import logo from '../../assets/dealroadshowLandingLogo.svg';

import drsUrl from '@drs/url';
import config from '@drs/config';
import { LOCALES } from '@drs/config/locales';
import DEALROADSHOW_HEADER_NAMESPACE from './locales/constant';
import en from './locales/en.json';
import ja from './locales/ja.json';

import { isScreen640 } from '@dealroadshow/uikit-styles/screen/screen';
import headerStyles from '@framework/ui/Pages/Landing/Legacy/Sections/Header/header.module.scss';
import stickyFooterStyles from '@framework/ui/Organisms/StickyFooter/stickyFooter.module.scss';
import styles from './layout.module.scss';

const LANGUAGES_OPTIONS: ILanguageOption[] = [
  { value: LOCALES.EN, label: 'ENG', countryCode: 'US' },
  { value: LOCALES.JA, label: '日本語', countryCode: 'JP' },
];

const Layout = ({ children }: PropsWithChildren) => {
  const router = useRouter();
  const entryCode = router.query.entryCode as string;
  const [isMobile, setIsMobile] = useState(isScreen640());
  const { t } = useTranslation(DEALROADSHOW_HEADER_NAMESPACE, { en, ja });

  const { replace, asPath } = useRouter();
  const loginUrl = useLoginUrlWithConsent(() => drsUrl.getLoginUrl());

  const onChangeLanguage = async (option: ILanguageOption) => {
    await replace(asPath, asPath, { locale: option.value });
  };

  const {
    colorSettings = {},
    getViewerCustomSettings,
    isPending,
    isCustomizationDisclaimerEnabled,
  } = useCustomizationContext();
  const isInvestorLogin = router.pathname === '/' || router.pathname.includes('/login/investor');

  const headerMenu = useMemo(() => ([
    {
      key: 'institutional',
      title: t('headerInstitutional'),
      path: '/',
      isActive: () => isInvestorLogin,
    },
    {
      key: 'retail',
      title: t('headerRetail'),
      path: '/retail-roadshows',
    },
  ]), [router.pathname, t]);

  useEffect(() => {
    if (entryCode) {
      getViewerCustomSettings(entryCode);
    }
  }, []);

  const loginLinkCls = cn(
    headerStyles.menuLink,
    headerStyles.menuIconLink,
    headerStyles.menuLoginButton,
    headerStyles.tenantDealroadshow,
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(isScreen640());
    window.addEventListener('resize', throttle(handleResize, 100));
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // TODO SSR: refactor this to enable SSR fro Header
  const shouldRenderHeader = !entryCode || (!isPending && !colorSettings?.viewerDisclaimerCustomizationEnabled);

  return (
    <>
      { shouldRenderHeader && (
        <Header
          logo={ logo.src }
          menu={ headerMenu }
          tenant={ config.code }
          loginButton={ () => (
            <div className={ styles.loginButtonContainer }>
              { isInvestorLogin && (
                <LanguageSwitcher
                  className={ headerStyles.menuLoginButton }
                  options={ LANGUAGES_OPTIONS }
                  onChange={ onChangeLanguage }
                />
              ) }
              <a
                href={ loginUrl }
                className={ loginLinkCls }
                data-test="headerLogIn"
              >
                { isMobile ? <IconUserLock className={ headerStyles.menuLoginIcon } /> :
                <IconLock className={ headerStyles.menuLoginIcon } /> }
                { t('headerDealManager') }
              </a>
            </div>
          ) }
        />
      ) }
      <div
        className={
        cn(styles.pageContainer, stickyFooterStyles.pageContainerWithStickyFooter, {
          [styles.pageContainerCustom]: isCustomizationDisclaimerEnabled && entryCode,
        })
      }
      >
        { children }
      </div>
      <Footer />
      <CookiesBanner />
    </>
  );
};

export default Layout;
